<template>
  <div class="scroll overflow-x-auto">
    <table class="w-full table-auto">
      <thead>
        <tr class="text-sm leading-normal">
          <template
            v-for="(title, key) in headers"
            :key="`header-${key}`">
            <th
              v-if="!title"
              class="py-3 px-4 text-center">
              <slot name="th-checkbox" />
            </th>
            <th
              v-else
              class="whitespace-nowrap bg-gray-200 text-gray-600 py-3 text-left"
              :class="[columnClasses[key] || headerPaddingClass, {'text-red-500': key === sort, 'cursor-pointer hover:bg-gray-300': sort}]"
              @click.stop="$emit('header-click', key)">
              {{ title }}
              <span
                v-if="key === sort">
                {{ order === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
          </template>
        </tr>
      </thead>
      <draggable
        v-if="items.length > 0"
        v-model="dataItems"
        v-bind="dragOptions"
        tag="transition-group"
        :component-data="{
          tag: 'tbody',
          name: 'data-list',
          ame: !drag ? 'flip-list' : null
        }"
        :disabled="dragDisabled"
        item-key="id"
        class="text-gray-600 text-sm font-light"
        @start="drag = true"
        @end="endDrag">
        <template #item="{ element }">
          <tr
            :class="{ 'cursor-pointer hover:bg-gray-100 hover:text-gray-400' : !hoverDisabled }"
            class="border-b border-gray-200">
            <td
              v-for="(title, key) in headers"
              :key="`row-${key}`"
              :class="[columnClasses[key], drag ? 'cursor-grabbing' : 'cursor-grab']"
              class="focus:cursor-grab py-3 text-center whitespace-nowrap"
              @click.stop="$emit('row-click', element, title)">
              <slot
                :name="key"
                :item="element">
                {{ element[key] }}
              </slot>
            </td>
          </tr>
        </template>
      </draggable>
      <tbody
        v-else-if="noDataText">
        <tr
          class="border-b border-gray-200 hover:bg-gray-100">
          <td
            :colspan="Object.keys(headers).length"
            class="py-3 px-6 text-center whitespace-nowrap pt-10 pb-10 text-gray-400 font-bold">
            {{ noDataText }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import draggable from 'vuedraggable'
import { ref, computed } from 'vue'

const props = defineProps({
  columnClasses: {
    type: Object,
    default: () => ({}),
  },
  headerPaddingClass: {
    type: String,
    default: 'px-6',
  },
  headers: {
    type: Object,
    required: true,
  },
  items: {
    type: Array,
    default: () => [],
  },
  noDataText: {
    type: String,
    default: '',
  },
  hoverDisabled: {
    type: Boolean,
    default: false,
  },
  dragDisabled: {
    type: Boolean,
    default: false,
  },
  sort: {
    type: String,
    default: '',
  },
  order: {
    type: String,
    default: '',
  },
  disableHover: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['row-click', 'sort-list', 'sort-item', 'header-click'])

const drag = ref(false)
const dragOptions = computed(() => ({
  animation: 200,
  ghostClass: 'ghost',
}))

const dataItems = computed({
  get: () => props.items,
  set: (newVal) => {
    emit('sort-list', newVal)
  },
})

const endDrag = (row) => {
  drag.value = false
  emit('sort-item', row.newDraggableIndex, row.oldDraggableIndex)
}
</script>

<style scoped>
.data-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
</style>
